
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Product } from '../types';
import { supabase, isSupabaseConfigured } from '../supabaseClient';
import { useLanguage } from './LanguageContext';
import { PRODUCTS } from '../constants';

interface ProductContextType {
  products: Product[];
  isLoading: boolean;
  addProduct: (product: Omit<Product, 'id'>) => Promise<boolean>;
  updateProduct: (id: number, updates: Partial<Product>) => Promise<boolean>;
  deleteProduct: (id: number) => Promise<void>;
  uploadImage: (file: File) => Promise<string | null>;
  refreshProducts: () => Promise<void>;
}

const ProductContext = createContext<ProductContextType | undefined>(undefined);

export const ProductProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [products, setProducts] = useState<Product[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { lang: uiLang } = useLanguage();

  const fetchProducts = async () => {
    setIsLoading(true);
    
    // Fallback to mock data if Supabase is not configured
    if (!isSupabaseConfigured) {
      console.log("Running in Mock Mode: Loading static products.");
      setProducts(PRODUCTS);
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .order('id', { ascending: false });

      if (error) {
        console.error("Supabase Product Fetch Error:", error.message, error.details);
        setProducts(PRODUCTS); // Fallback on error
      } else if (data) {
        setProducts(data);
      }
    } catch (e: any) {
      console.error("Unexpected Product Fetch Exception:", e.message);
      setProducts(PRODUCTS); // Fallback on exception
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const refreshProducts = async () => {
    await fetchProducts();
  };

  const uploadImage = async (file: File) => {
    if (!isSupabaseConfigured) return URL.createObjectURL(file);

    const fileExt = file.name.split('.').pop();
    const fileName = `prod-${Date.now()}-${Math.random().toString(36).substring(2, 7)}.${fileExt}`;
    const { error: uploadError } = await supabase.storage.from('product-images').upload(fileName, file);
    if (uploadError) {
      console.error("Storage Upload Error:", uploadError.message);
      return null;
    }
    const { data } = supabase.storage.from('product-images').getPublicUrl(fileName);
    return data.publicUrl;
  };

  const addProduct = async (productData: any): Promise<boolean> => {
    if (!isSupabaseConfigured) {
      alert("Mock Mode: Cannot save to database.");
      return false;
    }

    const payload = {
      ...productData,
      lang: productData.lang || uiLang
    };

    const { error } = await supabase.from('products').insert([payload]);
    if (error) {
      console.error("Supabase Add Product Error:", error.message, error.details);
      alert(`儲存商品失敗！\n錯誤原因：${error.message}\n提示：${error.hint || '請檢查資料庫欄位定義。'}`);
      return false;
    }
    
    await fetchProducts();
    return true;
  };

  const updateProduct = async (id: number, updates: any): Promise<boolean> => {
    if (!isSupabaseConfigured) {
      alert("Mock Mode: Cannot update database.");
      return false;
    }

    const { error } = await supabase.from('products').update(updates).eq('id', id);
    if (error) {
      console.error("Supabase Update Product Error:", error.message);
      alert(`更新商品失敗！\n錯誤原因：${error.message}`);
      return false;
    }
    
    await fetchProducts();
    return true;
  };

  const deleteProduct = async (id: number) => {
    if (!isSupabaseConfigured) {
      alert("Mock Mode: Cannot delete from database.");
      return;
    }

    if (!confirm("確定要刪除此商品嗎？")) return;
    const { error } = await supabase.from('products').delete().eq('id', id);
    if (error) {
      console.error("Delete Product Error:", error.message);
    } else {
      fetchProducts();
    }
  };

  return (
    <ProductContext.Provider value={{ products, isLoading, addProduct, updateProduct, deleteProduct, uploadImage, refreshProducts }}>
      {children}
    </ProductContext.Provider>
  );
};

export const useProducts = () => {
  const context = useContext(ProductContext);
  if (!context) throw new Error('useProducts must be used within ProductProvider');
  return context;
};
